import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Avatar,
} from "@mui/material";
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import TaskCalendar from "../task-calendar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { IoMdInformationCircle } from "react-icons/io";
import SelectedTaskDetailsManager from "./managerTaskDetails/selected-tast-details-manager";
import { filterTasksByDateRange } from "../../../utils/dateFilterUtils";
import SelectedTaskExportManager from "./managerTasksExport/selected-task-export-manager";
import Loading from "../../../common/loader/loading";

const ManagerVehicleCheckView = ({ onClose, selectedMainTask }) => {
  const [tasks, setTasks] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null); // State for expanded row
  const [loading, setLoading] = useState(true);
  const [openCalendar, setOpenCalendar] = useState(true); // Toggle calendar
  const [selectedTask, setSelectedTask] = useState(null); // State for selected task
  const [currentDate, setCurrentDate] = useState(new Date()); // Current month and year
  const [selectedDates, setSelectedDates] = useState([]); // Selected dates
  const [filteredTasks, setFilteredTasks] = useState([]); // Tasks after filtering and sorting

  // Handle date selection
  const handleSelectedDatesUpdate = (dates) => {
    setSelectedDates(dates);
    const updatedTasks = filterTasksByDateRange(tasks, dates, "created_at"); // Use the utility function
    setFilteredTasks(updatedTasks);
  };

  // Handle month navigation
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/vehicle/vehicleInspectionAll");
      if (response.data.message && response.data.data) {
        setTasks(response.data.data);
        setFilteredTasks(response.data.data);
      } else {
        console.error("Invalid response format:", response.data);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <Grid container spacing={2} mt={2}>
      <Grid item lg={9} md={8} xs={12}>
        <Box sx={{ p: 4, ml: 2, mr: 2 }}>
          <Typography sx={{ fontSize: "14px" }}>
            <Grid container>
              <Grid item>
                <KeyboardBackspaceIcon onClick={onClose} style={{ cursor: "pointer" }} />
              </Grid>
              <Grid item> Back to home </Grid>
            </Grid>
          </Typography>
          <Box mt={3} display='flex' justifyContent='space-between' mb={2}>
            <Box>
              <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "black" }} align='left' mt={1}>
                Weekly Vehicle Check List
              </Typography>
            </Box>
            <Box display='flex' gap={2}>
              <Button
                variant='outlined'
                color='primary'
                onClick={() => {
                  setOpenCalendar(true);
                  setSelectedTask(null);
                }}
              >
                <CalendarMonthOutlinedIcon />
                Calendar
              </Button>
            </Box>
          </Box>

          {filteredTasks.map((task) => (
            <Box key={task.id}>
              <Paper
                elevation={3}
                sx={{
                  padding: "20px 25px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  mb: "10px",
                }}
                onClick={() => setExpandedRow(expandedRow === task.id ? null : task.id)}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant='subtitle1' sx={{ fontWeight: "bold", fontSize: "16px" }}>
                    Weekly Vehicle Check List
                  </Typography>
                </Box>

                <Box sx={{ flex: 2, textAlign: "center" }}>
                  <Typography variant='body2' color='black'>
                    {new Date(task?.created_at).toISOString().split("T")[0]}
                  </Typography>
                </Box>

                <Grid container sx={{ flex: 1 }}>
                  <Grid item>
                    <Avatar
                      alt='Profile'
                      src='/images/person.png'
                      className='profile-avatar'
                      sx={{ width: 40, height: 40, marginRight: 2 }}
                    />
                  </Grid>
                  <Grid item> {task?.completed_by}</Grid>
                </Grid>

                <Box sx={{ textAlign: "right" }}>
                  <IconButton sx={{ color: "black" }}>
                    {expandedRow === task.id ? <ExpandLessIcon /> : <ExpandCircleDownIcon />}
                  </IconButton>
                </Box>
              </Paper>

              <Collapse in={expandedRow === task.id} timeout='auto' unmountOnExit>
                <Box sx={{ p: 1 }}>
                  <Table>
                    <TableBody>
                      {task?.inspectionDetails?.map((item, index) => (
                        <TableRow
                          key={index}
                          onClick={() => {
                            setSelectedTask({
                              ...task,
                              selectedItem: item,
                              taskName: "Weekly Vehicle Check List",
                              mainTask: selectedMainTask,
                            });
                            setOpenCalendar(false);
                          }}
                        >
                          <TableCell>{item.question}</TableCell>
                          <TableCell>
                            {item?.condition === "No" && (
                              <IconButton color='error'>
                                <IoMdInformationCircle />
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton color='primary' sx={{ color: "#1751D0" }}>
                              <CheckCircleIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </Box>
          ))}
        </Box>
      </Grid>
      <Grid item lg={3} md={4} xs={12}>
        {openCalendar && (
          <>
            <Box sx={{ p: 4, mr: 2, ml: 2 }}>
              <TaskCalendar
                currentDate={currentDate}
                selectedDates={selectedDates}
                handlePrevMonth={handlePrevMonth}
                handleNextMonth={handleNextMonth}
                handleSelectedDatesUpdate={handleSelectedDatesUpdate}
              />
            </Box>
            <Box sx={{ p: 2, mr: 2, mt: 3, ml: 2 }}>
              <SelectedTaskExportManager vehicleTasks={filteredTasks} taskName='  Vehicle Check List' />
            </Box>
          </>
        )}

        {selectedTask && (
          <>
            <Box sx={{ p: 2, mr: 2, ml: 2 }}>
              <SelectedTaskDetailsManager selectedTask={selectedTask} />
            </Box>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default ManagerVehicleCheckView;
